import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import config from "../../_config.js";
import LogoImage from "../../assets/images/Logo blue.png";

export default function ReportDownload() {
  const { trackingCode } = useParams();
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [reportMeta, setReportMeta] = useState(null);

  // No calls on load; only fetch on button click

  async function onDownload() {
    setDownloading(true);
    try {
      setError(null);
      setPhase("fetching");
      const pdfUrl = `${config.appapi}/v1/action-plan-v2/manager/get-pdf/?tracking_code=${encodeURIComponent(
        trackingCode
      )}`;
      const dJson = await fetchJson(pdfUrl);
      setReportMeta(dJson || null);
      const url = dJson?.presigned_url || dJson?.download_url || dJson?.url;
      if (!url) throw new Error("No download URL returned.");
      window.open(url, "_blank");
      setPhase("ready");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ReportDownload] download error", e?.status, e?.detail || e);
      const friendly = e?.status === 404
        ? "We couldn't find a report for this link. It may be invalid or expired."
        : e?.status === 401 || e?.status === 403
        ? "Your download link is not authorized or has expired."
        : e?.status >= 500
        ? "We couldn't generate the download link due to a server issue. Try again later."
        : "Failed to generate the PDF download link. Please try again.";
      setError(friendly);
      setPhase("error");
    } finally {
      setDownloading(false);
    }
  }

  async function fetchJson(url) {
    const res = await fetch(url);
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      const err = new Error("Request failed");
      err.status = res.status;
      err.detail = text ? text.slice(0, 300) : "";
      throw err;
    }
    if (contentType.includes("application/json")) return res.json();
    // Try to parse as JSON anyway; if it fails it's likely an HTML fallback
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      const err = new Error("Unexpected non-JSON response");
      err.status = res.status;
      err.detail = text ? text.slice(0, 300) : "";
      throw err;
    }
  }

  return (
    <div style={styles.wrapper}>
      {/* Background layer with subtle blobs */}
      <div style={styles.bgLayer} aria-hidden>
        <div style={styles.blobLeft} />
        <div style={styles.blobRight} />
      </div>
      <div style={styles.brand}>
        <img src={LogoImage} alt="Innerlogic" style={styles.logo} />
      </div>
      <div style={styles.card}>
        <h2 style={styles.title}>Your Report</h2>
        {error && (
          <>
            <p style={styles.error}>{error}</p>
            <p style={styles.subtle}>
              If this keeps happening, please request a new link.
            </p>
          </>
        )}
        <p style={styles.subtle}>Click below to download your PDF.</p>
        <button
          style={styles.button}
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading
            ? "Generating Download Link…"
            : "Download Report PDF"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8f9fa", // match basic top banner tone
    padding: 16,
    position: "relative",
    overflow: "hidden",
  },
  bgLayer: {
    position: "absolute",
    inset: 0,
    top: -96,
    height: 320,
    pointerEvents: "none",
    zIndex: 0,
  },
  blobLeft: {
    position: "absolute",
    left: "-8%",
    top: 0,
    width: 320,
    height: 320,
    borderRadius: 9999,
    background: "rgba(99,102,241,0.16)", // indigo tint
    filter: "blur(60px)",
  },
  blobRight: {
    position: "absolute",
    right: "-6%",
    top: 24,
    width: 288,
    height: 288,
    borderRadius: 9999,
    background: "rgba(34,211,238,0.16)", // cyan tint
    filter: "blur(60px)",
  },
  brand: {
    position: "fixed",
    top: 16,
    left: 16,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logo: { height: 32, width: "auto" },
  card: {
    width: "100%",
    maxWidth: 520,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
    padding: 24,
    position: "relative",
    zIndex: 1,
  },
  title: { margin: "0 0 12px", fontSize: 22 },
  subtle: { color: "#6b7280", marginTop: 4 },
  error: { color: "#b91c1c", marginBottom: 8 },
  button: {
    marginTop: 16,
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 14px",
    cursor: "pointer",
  },
};

